import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Avatar,
  VStack,
  HStack,
  Checkbox,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../configs/axios.config";
import {
  dispatchTripToAllDrivers,
} from "../../actions/dispatch.actions";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";
import {
  DRIVER_OPERATIONAL_STATUS_DISPATCH_LABEL,
} from "../../constants/driverStatus";



const TARGET_TYPE = {
  DRIVER: "driver",
  VENDOR: "vendor",
};

const isMongoObjectId = (value) =>
  typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);

const formatDurationValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value} min`;
  }

  const text = String(value).trim();
  if (!text) return null;

  if (/^\d+(\.\d+)?$/.test(text)) {
    return `${text} min`;
  }

  return text;
};

const getCandidateType = (candidate) =>
  candidate?.candidateType || candidate?.targetType || TARGET_TYPE.DRIVER;

const getCandidateKey = (candidate) =>
  `${getCandidateType(candidate)}:${candidate?._id || candidate?.targetId}`;

const AvailableDriver = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { tripId, tripData } = location.state || {};
  const resolvedTripId =
    tripData?._id ||
    tripData?.mongoId ||
    tripData?.id ||
    (isMongoObjectId(tripId) ? tripId : null);
  const [searchInput, setSearchInput] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loadingBookingDetails, setLoadingBookingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDrivers, setTotalDrivers] = useState(0);

  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const effectiveTripData = bookingDetails || tripData || {};
  const vehicleSegmentId =
    effectiveTripData?.vehicleSegment?._id ||
    (isMongoObjectId(effectiveTripData?.vehicleSegment)
      ? effectiveTripData.vehicleSegment
      : null);

  const SortIcon = () => (
    <Box ml="4px" display="flex" flexDirection="column">
      <TriangleUpIcon boxSize="7px" color="#CBD5E1" />
      <TriangleDownIcon boxSize="7px" color="#CBD5E1" mt="1px" />
    </Box>
  );

  const loadDrivers = async () => {
    setFetching(true);

    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const trimmedSearch = searchInput.trim();
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        debug: 1,
        includeVendors: true,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      };

      // Include vehicleSegmentId if available
      if (vehicleSegmentId) {
        params.vehicleSegmentId = vehicleSegmentId;
      }

      // Include bookingId if available
      if (resolvedTripId) {
        params.bookingId = resolvedTripId;
      }

      const response = await axios.get(`/inventory/drivers/available`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const drivers = response?.data?.data ?? [];

      console.log("DRIVERS:", drivers.length);

      setDrivers(drivers);
      setTotalPages(response.data.totalPages);
      setTotalDrivers(response.data.totalDrivers);

    } catch (error) {
      console.error("Failed to load available drivers", error);

      setDrivers([]);
      setSelectedDrivers([]);
      setTotalPages(1);
      setTotalDrivers(0);

      toast({
        title: "Failed to fetch available drivers",
        description:
          error.response?.data?.message ||
          error.message ||
          "Could not load available drivers",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFetching(false);
    }
  };

  const loadBookingDetails = async () => {
    if (!resolvedTripId) return;

    setLoadingBookingDetails(true);
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const res = await axios.get(`/management/booking`, {
        params: { id: resolvedTripId },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const details = res?.data?.data || res?.data || tripData || null;
      setBookingDetails(details);
      return details;
    } catch (error) {
      console.error("Error fetching booking details:", error);
      setBookingDetails(tripData || null);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch booking details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return tripData || null;
    } finally {
      setLoadingBookingDetails(false);
    }
  };

  useEffect(() => {
    if (!vehicleSegmentId && resolvedTripId) {
      loadBookingDetails();
    }
  }, [resolvedTripId, vehicleSegmentId]);

  useEffect(() => {
    loadDrivers();
  }, [currentPage, itemsPerPage, vehicleSegmentId]);

  useEffect(() => {
    if (isConfirmModalOpen) {
      loadBookingDetails();
    }
  }, [isConfirmModalOpen, resolvedTripId]);

  const handleSendRequestClick = (candidate) => {
    setSelectedDrivers([getCandidateKey(candidate)]);
    setSelectedDriver(candidate);
    setIsConfirmModalOpen(true);
  };

  const toggleDriver = (key) => {
    setSelectedDrivers((prev) =>
      prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key,
      ],
    );
  };

  const handleSelectAllDrivers = () => {
    const selectableDrivers = drivers.filter((d) => !d.isDispatched);
    const currentDriverIds = selectableDrivers.map(getCandidateKey);
    const allSelected =
      currentDriverIds.length > 0 &&
      currentDriverIds.every((id) => selectedDrivers.includes(id));

    if (allSelected) {
      setSelectedDrivers((prev) =>
        prev.filter((id) => !currentDriverIds.includes(id)),
      );
      return;
    }

    setSelectedDrivers((prev) => [
      ...new Set([...prev, ...currentDriverIds]),
    ]);
  };

  const handleDispatchAllClick = () => {
    if (selectedDrivers.length === 0) {
      toast({
        title: "No drivers selected",
        description: "Please check at least one driver or vendor",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedDriver(null);
    setBookingDetails(null);
    setIsConfirmModalOpen(true);
  };

  const handleDispatchToAllDrivers = async () => {
    if (!resolvedTripId) {
      toast({ title: "Trip ID missing", status: "error" });
      return;
    }

    if (selectedDrivers.length === 0) {
      toast({ title: "No drivers selected", status: "error" });
      return;
    }

    setIsAssigning(true);
    try {
      const selectedTargets = selectedDrivers
        .map((key) => {
          const [type, id] = key.split(":");
          return { type, id };
        })
        .filter((target) => target.id);

      const res = await dispatchTripToAllDrivers({
        bookingId: resolvedTripId,
        targets: selectedTargets,
        driverIds: selectedTargets
          .filter((target) => target.type === TARGET_TYPE.DRIVER)
          .map((target) => target.id),
        vendorIds: selectedTargets
          .filter((target) => target.type === TARGET_TYPE.VENDOR)
          .map((target) => target.id),
        tripData: bookingDetails || tripData,
      });

      toast({
        title: "Dispatch Sent",
        description:
          res?.message || "Booking sent to selected drivers/vendors",
        status: "success",
      });

      setIsConfirmModalOpen(false);
      navigate("/dispatch");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleConfirmDispatch = () => {
    handleDispatchToAllDrivers();
  };

  const handleCancelAssignment = () => {
    setIsConfirmModalOpen(false);
    setSelectedDriver(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      loadDrivers();
    }
  };

  const standardOptions = [9, 20, 50];
  const perPageOptions = (() => {
    if (totalDrivers === 0) return standardOptions;
    if (totalDrivers < standardOptions[0]) return [totalDrivers];
    const filtered = standardOptions.filter((num) => num <= totalDrivers);
    return filtered;
  })();

  const estimatedTimeLabel =
    formatDurationValue(bookingDetails?.estimatedTime) ||
    formatDurationValue(bookingDetails?.tripDuration) ||
    formatDurationValue(tripData?.estimatedTime) ||
    formatDurationValue(tripData?.tripDuration) ||
    "N/A";
  const vehicleTypeLabel =
    effectiveTripData?.vehicleType ||
    effectiveTripData?.vehicleSegment?.segmentName ||
    "N/A";

  return (
    <Box minH="100vh" p={8}>
      <Box borderRadius="12px" p={6} boxShadow="sm" mt={2}>
        {/* Header Section */}
        <Box mb={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <BreadCrumbMaker
              items={[
                { title: "Dispatcher", link: "/dispatch" },
                {
                  title: "Available Drivers & Vendors",
                  link: "/dispatch/available-drivers",
                  on: true,
                },
              ]}
            />
            <Button
              bg="#396AFF"
              color="white"
              borderRadius="8px"
              px={6}
              py={3}
              fontSize="14px"
              fontWeight="600"
              height="40px"
              fontFamily="inter, sans-serif"
              _hover={{ bg: "#396AFF" }}
              _active={{ bg: "#396AFF" }}
              onClick={handleDispatchAllClick}
              isDisabled={selectedDrivers.length === 0}
            >
              Dispatch to selected
            </Button>
          </Flex>
        </Box>

        {/* <Box w="338px" h="55px" bg="#ECF0FF" borderRadius="4px" display="flex" gap="10px" alignItems="center" mb={6}>
          <Box
            w="155px"
            h="39px"
            bg={activeTab === 'unassigned' ? '#FFFFFF' : 'transparent'}
            color={activeTab === 'unassigned' ? '#333B69' : '#333B69'}
            borderRadius="4px"
            p="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            fontWeight="600"
            fontSize="14px"
            fontFamily="inter, sans-serif"
            onClick={() => {
              setActiveTab('unassigned');
              setCurrentPage(1);
            }}
            transition="all 0.2s"
          >
            Unassigned Trips
          </Box>
          <Box
            w="155px"
            h="39px"
            bg={activeTab === 'assigned' ? '#FFFFFF' : 'transparent'}
            color="#333B69"
            borderRadius="4px"
            p="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            fontWeight="600"
            fontSize="14px"
            fontFamily="inter, sans-serif"
            onClick={() => {
              setActiveTab('assigned');
              setCurrentPage(1);
            }}
            transition="all 0.2s"
          >
            Assigned Trips
          </Box>
        </Box> */}

        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={4}>
            <Box>
              <Text
                fontSize="14px"
                fontWeight="500"
                color="#174277"
                fontFamily="inter, sans-serif"
                mb={1}
              >
                Select Driver or Vendor for Trip : {effectiveTripData?.bookingID || tripId || "N/A"}
              </Text>
              <Text
                fontSize="14px"
                fontWeight="500"
                color="#174277"
                fontFamily="inter, sans-serif"
              >
                Require Vehicle Type : {vehicleTypeLabel}
              </Text>
            </Box>
          </Flex>
        </Flex>

        {/* Search Bar */}
        <Box mb={6}>
          <InputGroup maxW="250px">
            <InputLeftElement pointerEvents="none" h="40px">
              <Search size={16} color="#64748B" />
            </InputLeftElement>
            <Input
              placeholder="Search Driver or Vendor"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              borderRadius="md"
              h="40px"
              bg="#FFFF"
              border="1px solid #E2E8F0"
              color="#64748B"
              fontSize="14px"
              _placeholder={{ color: "#64748B" }}
              _focus={{ outline: "none", boxShadow: "0 0 0 1px" }}
            />
          </InputGroup>
        </Box>
        <Box bg="white" borderRadius="12px" p={6} boxShadow="sm">
          {/* Loading Indicator */}
          {fetching && <Loading />}

          {!fetching && (
            <Box overflowX="auto" mb={6}>
              <Table variant="simple" bg="white" fontFamily="inter, sans-serif">
                <Thead>
                  <Tr>
                    <Th
                      textTransform="none"
                      color="#718EBF"
                      fontSize="16px"
                      fontWeight="500"
                      py={4}
                      borderBottom="2px solid #E5E5E5"
                    >
                      <Checkbox
                        isChecked={
                          drivers.filter((d) => !d.isDispatched).length > 0 &&
                          drivers.filter((d) => !d.isDispatched).every((d) => selectedDrivers.includes(getCandidateKey(d)))
                        }
                        isIndeterminate={
                          selectedDrivers.length > 0 &&
                          drivers.filter((d) => !d.isDispatched).some((d) => selectedDrivers.includes(getCandidateKey(d))) &&
                          !drivers.filter((d) => !d.isDispatched).every((d) => selectedDrivers.includes(getCandidateKey(d)))
                        }
                        onChange={handleSelectAllDrivers}
                      />
                    </Th>
                    {[
                      "Name",
                      "Type",
                      "Vehicle No.",
                      "Mobile No.",
                      "Current Location",
                      "Rating",
                      "Status",
                      "Next Availability",
                      "Action",
                    ].map((head, index) => (
                      <Th
                        key={head}
                        textTransform="none"
                        color="#718EBF"
                        fontSize="16px"
                        fontWeight="500"
                        py={4}
                        borderBottom="2px solid #E5E5E5"
                        borderTopLeftRadius={index === 0 ? "8px" : undefined}
                        borderTopRightRadius={index === 8 ? "8px" : undefined}
                      >
                        <Flex align="center" gap={2}>
                          <Text>{head}</Text>
                          <SortIcon />
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                </Thead>

                <Tbody>
                  {drivers.length > 0 ? (
                    drivers.map((driver) => {
                      const candidateKey = getCandidateKey(driver);
                      const candidateType = getCandidateType(driver);
                      const statusLabel =
                        driver.statusLabel ||
                        DRIVER_OPERATIONAL_STATUS_DISPATCH_LABEL[
                          driver.aditionalDetails?.operationalStatus
                        ] ||
                        "N/A";
                      const isAvailable = statusLabel === "Available" || statusLabel === "Active";

                      return (
                      <Tr
                        key={candidateKey}
                        _hover={{ bg: "#F8FAFC" }}
                        borderBottom="1px solid #E5E5E5"
                      >
                        <Td py={4} border="none">
                          <Checkbox
                            isChecked={selectedDrivers.includes(candidateKey)}
                            onChange={() => toggleDriver(candidateKey)}
                            isDisabled={driver.isDispatched}
                          />
                        </Td>
                        <Td py={4} border="none">
                          {driver.name || driver.vendorName || "N/A"}
                        </Td>

                        <Td py={4} border="none">
                          <Badge
                            bg={candidateType.includes("vendor") ? "#E0E7FF" : "#D1FAE5"}
                            color={candidateType.includes("vendor") ? "#3730A3" : "#047857"}
                            textTransform="capitalize"
                          >
                            {candidateType}
                          </Badge>
                        </Td>

                        <Td py={4} border="none">
                          {driver.vehicleDetails?.vehicleNumber || "N/A"}
                        </Td>

                        <Td py={4} border="none">
                          {driver.contactDetails?.mobileNumber || "N/A"}
                        </Td>

                        <Td py={4} border="none">
                          {driver.currentLocation?.address || "N/A"}
                        </Td>

                        <Td py={4} border="none">
                          <Badge bg="#FEF3C7" color="#92400E">
                            ⭐ {driver.aditionalDetails?.driverRating ?? "N/A"}
                          </Badge>
                        </Td>

                        <Td py={4} border="none">
                          <Badge
                            bg={
                              isAvailable
                                ? "#D1FAE5"
                                : "#FEF3C7"
                            }
                            color={
                              isAvailable
                                ? "#059669"
                                : "#92400E"
                            }
                            fontSize="11px"
                            fontWeight="600"
                            px={3}
                            py={1.5}
                            borderRadius="4px"
                            textTransform="capitalize"
                          >
                            {statusLabel}
                          </Badge>
                        </Td>

                        <Td py={4} border="none">
                          {driver.nextAvailability || "—"}
                        </Td>

                        <Td py={4} border="none">
                          <Button
                            size="sm"
                            bg={driver.isDispatched ? "#E2E8F0" : "#396AFF"}
                            color={driver.isDispatched ? "#64748B" : "white"}
                            borderRadius="6px"
                            onClick={() => handleSendRequestClick(driver)}
                            isDisabled={driver.isDispatched}
                            _hover={driver.isDispatched ? { bg: "#E2E8F0" } : { bg: "#254EDB" }}
                          >
                            {driver.isDispatched ? "Dispatched" : "Send Request"}
                          </Button>
                        </Td>
                      </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={10} textAlign="center" py={8}>
                        <Text color="#718EBF">No drivers or vendors found</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={2}>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                Showing:
              </Text>
              <Select
                size="sm"
                width="70px"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                borderRadius="6px"
                border="1px solid #E9E7FD"
                _focus={{ outline: "none", borderColor: "#00B69B" }}
              >
                {perPageOptions.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </Select>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                of{" "}
                <Text as="span" fontWeight="600" color="#8B909A">
                  {totalDrivers}
                </Text>
              </Text>
            </Flex>
            <Flex gap={2}>
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                &lt;
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    bg={pageNum === currentPage ? "#0F60FF" : "#F1F2F6"}
                    color={pageNum === currentPage ? "white" : "#8B909A"}
                    border="1px solid"
                    borderColor={
                      pageNum === currentPage ? "#0F60FF" : "#E9E7FD"
                    }
                    _hover={{
                      bg: pageNum === currentPage ? "#0F60FF" : "#E9E7FD",
                    }}
                    w="36px"
                    h="36px"
                    borderRadius="6px"
                    fontWeight="500"
                    fontFamily="'Public Sans', sans-serif"
                    fontSize="15px"
                    lineHeight="100%"
                    letterSpacing="0.43px"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelAssignment}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" />

        <ModalContent borderRadius="12px" p={6} width="477px" height="603px">
          {/* Header */}
          <ModalHeader
            p={0}
            marginTop="-10px"
            borderBottom="1px solid"
            borderColor="#D0D5DD"
          >
            <Text
              fontSize="18px"
              fontFamily="Plus Jakarta Sans"
              fontWeight="600"
              color="gray.900"
            >
              Confirm Dispatch Request
            </Text>
            <Text
              fontSize="12px"
              fontFamily="Plus Jakarta Sans"
              color="#859BB7"
              mt={1}
            >
              Send this booking request without force-assigning the trip.
            </Text>
          </ModalHeader>

          <ModalBody p={0} mt={6}>
            {loadingBookingDetails ? (
              <Flex justify="center" py={10}>
                <Loading />
              </Flex>
            ) : (
              <>
                {/* Trip ID */}

                {/* Rider */}
                <HStack spacing={3} mb={6}>
                  <Avatar
                    size="sm"
                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=skylar"
                    name={bookingDetails?.customer?.name || bookingDetails?.customer || "Passenger"}
                  />
                  <Box>
                    <Text
                      fontSize="12px"
                      color="#718EBF"
                      fontFamily="inter, sans-serif"
                    >
                      Trip ID: {bookingDetails?.bookingID || resolvedTripId || "T01234"}
                    </Text>
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      fontFamily="inter, sans-serif"
                    >
                      {bookingDetails?.customer?.name || bookingDetails?.customer || "Passenger"}
                    </Text>
                  </Box>
                </HStack>

                {/* Dispatch target */}
                {selectedDriver && (
                  <HStack spacing={3} mb={6}>
                    <Avatar
                      size="sm"
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedDriver.name || selectedDriver.vendorName}`}
                      name={selectedDriver.name || selectedDriver.vendorName}
                    />
                    <Box>
                      <Text
                        fontSize="12px"
                        color="#718EBF"
                        fontFamily="inter, sans-serif"
                      >
                        {getCandidateType(selectedDriver) === TARGET_TYPE.VENDOR ? "Vendor" : "Driver"}
                      </Text>
                      <Text fontSize="14px" fontWeight="500">
                        {selectedDriver.name || selectedDriver.vendorName}
                      </Text>
                    </Box>
                  </HStack>
                )}

                {/* Locations */}
                <VStack align="stretch" spacing={4} mb={6}>
                  <HStack spacing={3}>
                    <Box w="10px" h="10px" bg="teal.400" borderRadius="full" />
                    <Box>
                      <Text
                        fontSize="14px"
                        fontWeight="500"
                        fontFamily="inter, sans-serif"
                      >
                        {bookingDetails?.pickupLocation || bookingDetails?.pickup || tripData?.pickup || "Delhi"}
                      </Text>
                      <Text
                        fontSize="12px"
                        color="#B0B0B0"
                        fontFamily="cabin, sans-serif"
                      >
                        {bookingDetails?.pickupAddress || "Pickup location"}
                      </Text>
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <Box w="10px" h="10px" bg="blue.400" borderRadius="full" />
                    <Box>
                      <Text fontSize="14px" fontWeight="500">
                        {bookingDetails?.dropLocation || bookingDetails?.drop || tripData?.drop || "Noida"}
                      </Text>
                      <Text fontSize="12px" color="gray.500">
                        {bookingDetails?.dropAddress || "Drop location"}
                      </Text>
                    </Box>
                  </HStack>
                </VStack>

                {/* Estimated Time */}
                <Text fontSize="14px" fontFamily="inter, sans-serif" mb={6}>
                  Estimate time : <b>{estimatedTimeLabel}</b>
                </Text>

                {/* Package */}
                <HStack
                  spacing={3}
                  p={3}
                  mb={6}
                  w="100%"
                  borderBottom="1px solid"
                  borderColor="#E2E8F0"
                >
                  <Box
                    w="32px"
                    h="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    📦
                  </Box>
                  <Box>
                    <Text fontSize="14px" fontWeight="500">
                      {bookingDetails?.packageName || "Standard"}
                    </Text>
                    <Text fontSize="12px" color="gray.500">
                      {bookingDetails?.packageType || "Basic +"}
                    </Text>
                  </Box>
                </HStack>

                {/* Disclaimer */}
                <Text
                  fontSize="12px"
                  color="#859BB7"
                  fontFamily="Plus Jakarta Sans"
                  lineHeight="1.6"
                >
                  By confirming, the booking request will be sent to the selected
                  driver or vendor. The trip is assigned only after the app-side
                  accept or vendor assignment flow completes.
                </Text>
                <Text
                  fontSize="10px"
                  isCentered
                  color="#B0B0B0"
                  fontFamily="inter, sans-serif"
                  textAlign="center"
                >
                  Clicking “confirm” will not force-assign the booking.
                </Text>
              </>
            )}
          </ModalBody>

          {/* Footer */}
          <ModalFooter p={0}>
            <HStack w="100%" spacing={3}>
              <Button
                variant="outline"
                flex={1}
                onClick={handleCancelAssignment}
                isDisabled={isAssigning}
              >
                Cancel
              </Button>

              <Button
                flex={1}
                bg="blue.500"
                color="white"
                _hover={{ bg: "blue.600" }}
                onClick={handleConfirmDispatch}
                isLoading={isAssigning}
              >
                Confirm
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AvailableDriver;
